import React, {
  useEffect, useReducer, useRef, useState, forwardRef, useImperativeHandle
} from 'react';
import Highcharts from 'highcharts';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '@services/firebase-service';
import { logToCloudWatch } from '@services/logger.service';
import { useStreamData } from '@lib/hooks/useStreamData';
import { pushDataLayerForEvent } from '@lib/gtag';
import WordChart from './WordChart';
import { handleEngagement } from '@services/interactions-service';

const EmojisEverywhere = forwardRef(({
  streamId,
  platformType,
  currentRosFeature,
  currentRosResult,
  interactionType,
  setCurrentFeatureId,
  startInteraction,
  splitDiv,
  dispatchTab,
  comments,
  isFullScreen,
  setShowRosModel,
  interactionState,
  moderationModeRef,
  activeInteractionRef,
  updateFSMeta,
  setShowNotification,
  setStartTime,
  showInstantCustomisationPopup = false,
  setTotalResponders
}, ref) => {
  const { isRos, isResult } = interactionType;

  const {
    firestoreUnsub, setFirestoreUnsub, setOverrideDismissStatus, setFeatureId
  } = useStreamData(s => ({
    firestoreUnsub: s.firestoreUnsub,
    setFirestoreUnsub: s.setFirestoreUnsub,
    setOverrideDismissStatus: s.setOverrideDismissStatus,
    setFeatureId: s.setFeatureId
  }));

  const [eeId, setEeId] = useState('');
  const [eeSettings, setEeSettings] = useState(currentRosFeature.setting);

  const [isPaused, _setIsPaused] = useState(false);
  const isPausedRef = useRef(isPaused);
  const setIsPaused = (data) => {
    isPausedRef.current = data;
    _setIsPaused(data);
  };

  const [exclusionList, _setExclusionList] = useState([]);
  const exclusionListRef = useRef(exclusionList);
  const setExclusionList = (data) => {
    exclusionListRef.current = [...exclusionListRef.current, data];
    _setExclusionList([...exclusionList, data]);
  };

  const [gtagTriggerCounter, setGtagTriggerCounter] = useState(0);

  const getEEData = () => {
    if (currentRosResult.length > 0 && currentRosResult[0].comments.length > 0) {
      return currentRosResult[0].comments;
    }
    return currentRosFeature.comments;
  };

  const highchartsRef = useRef(null);

  const buildChartOptions = ({ data, displayStyle }) => ({
    series: [{
      type: 'wordcloud',
      data,
      name: 'Count'
    }],
    chart: {
      backgroundColor: 'rgba(255, 255, 255, 0)'
    },
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 1400
        },
        chartOptions: {
          legend: {
            enabled: false
          }
        }
      }]
    },
    plotOptions: {
      series: {},
      chart: { height: '100%' },
      wordcloud: {
        displayStyle: displayStyle || 'chips',
        rotation: {
          from: 0,
          orientations: 1,
          to: 0
        }
      }
    }
  });

  const options = {
    exporting: { enabled: false },
    title: { text: '' },
    ...buildChartOptions({
      data: getEEData(),
      displayStyle: eeSettings?.wordCloudDisplayStyle
    })
  };

  const [chartOptions, dispatch] = useReducer((state, action) => {
    if (action.data?.length > 0) {
      setGtagTriggerCounter(1);
      if (gtagTriggerCounter === 0) pushDataLayerForEvent('populate_comments_emojis_everywhere');
    }

    if (action.type === 'UPDATE_SETTING') {
      return {
        ...state,
        ...buildChartOptions({
          data: action?.data || state.series[0]?.data || [],
          displayStyle: action?.settings?.wordCloudDisplayStyle || eeSettings?.wordCloudDisplayStyle
        })
      };
    }

    return {
      ...state,
      ...buildChartOptions({
        data: action.data,
        displayStyle: eeSettings?.wordCloudDisplayStyle
      })
    };
  }, options);

  const setPreviousData = ({ interactionResult = [] }) => {
    if (interactionResult.length > 0 && interactionResult[0].comments.length > 0) {
      dispatch({ type: 'UPDATE_DATA', data: interactionResult[0].comments });
    } else {
      dispatch({ type: 'UPDATE_DATA', data: [] });
    }
  };

  const setupEmojisEverywhere = ({ id, settings, noOfResponders, interactionResult }) => {
    dispatch({ data: [] });

    setFeatureId(id);
    setCurrentFeatureId(id);
    setEeId(id);
    if (settings) setEeSettings(settings);

    setPreviousData({ interactionResult });

    setTotalResponders(noOfResponders);
  };

  const handleEmojisEverywhere = async ({ interactionId = null, interactionResult = [] }) => {
    const response = await handleEngagement({ streamId, platformType, currentRosFeature, interactionId });

    if (response.status && response.entity) {
      const interactionDetails = {
        id: response.entity.engagementId,
        settings: currentRosFeature.setting,
        colors: currentRosFeature.color,
        noOfResponders: response.entity.noOfResponders || 0,
        interactionResult
      };
      setupEmojisEverywhere(interactionDetails);
      updateFSMeta({ activeInteraction: { ...interactionDetails, type: 'emojisEverywhere' } });
    }
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  useEffect(() => {
    setPreviousData({ interactionResult: currentRosResult });
  }, [currentRosFeature.id, currentRosResult]);

  useEffect(() => {
    if (currentRosFeature.setting) {
      setEeSettings(currentRosFeature.setting);
      dispatch({ type: 'UPDATE_SETTING', settings: currentRosFeature.setting, data: currentRosFeature.comments });
    }
  }, [currentRosFeature.setting]);

  useEffect(() => {
    if (eeId && isRos) {
      const logData = {
        streamId,
        interactionType: 'emojisEverywhere',
        interactionId: eeId
      };
      setFirestoreUnsub({
        unsub: onSnapshot(doc(db, 'streams', streamId, 'emojisEverywhere', eeId), (document) => {
          if (document.exists() && !isPausedRef.current) {
            const dataList = document.data().data
              .filter((x) => !exclusionListRef.current.includes(x.name));

            if (dataList.length > 0) setShowNotification(false);

            dispatchTab({ type: 'UPDATE_TAB', data: dataList });
            dispatch({ type: 'UPDATE_DATA', data: dataList });

            setTotalResponders(document.data().noOfResponders || 0);
          }
        }, (error) => logToCloudWatch('Failed to subscribe to firestore', { ...logData, error }, 'ERROR'))
      });
    }
    return () => {
      unsubscribeFromFirestore();
    };
  }, [eeId, isPausedRef.current]);

  const SendDataToGoogleAnalytics = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    pushDataLayerForEvent('end_emojis_everywhere_interaction', { no_comments: chart?.series[0]?.data?.length });
  };

  const stopEmojisEverywhere = async () => {
    unsubscribeFromFirestore();
    // const sessionId = localStorage.getItem('sessionId');
    // await stopEngagement(streamId, 'emojisEverywhere', { engagementId: eeId, platformType, sessionId });
  };

  const onPlayClick = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    chart?.series[0]?.chart?.delImgs?.forEach((img) => {
      img.hide();
    });

    if (chart?.series[0]?.chart?.delImgs) { chart.series[0].chart.delImgs = []; }
  };

  useEffect(() => {
    if (activeInteractionRef.current?.id && activeInteractionRef.current?.type === 'wordCloud') {
      setupEmojisEverywhere({
        id: activeInteractionRef.current.id,
        colors: activeInteractionRef.current.colors,
        settings: activeInteractionRef.current.settings,
        noOfResponders: activeInteractionRef.current.noOfResponders || 0,
        interactionResult: activeInteractionRef.current.currentRosResult || []
      });
    }
  }, [activeInteractionRef.current]);

  const handleStartInteraction = () => {
    if (currentRosResult && currentRosResult.length > 0) {
      handleEmojisEverywhere({ interactionId: currentRosResult[0].id, interactionResult: currentRosResult });
    } else {
      handleEmojisEverywhere({ interactionId: null, interactionResult: [] });
    }
    setStartTime(Date.now());
  };

  const handleStopInteraction = () => {
    stopEmojisEverywhere();
    setIsPaused(false);
    onPlayClick();
    SendDataToGoogleAnalytics();
    setEeId('');
  };

  const manageEmojisEverywhereInteraction = () => {
    if (!isResult && startInteraction) {
      handleStartInteraction();
    } else if (eeId) {
      handleStopInteraction();
    } else if (currentRosResult && currentRosResult.length > 0) {
      setTotalResponders(currentRosResult[0].noOfResponders || 0);
    }
  };

  useEffect(() => {
    if (!currentRosFeature.id) return;

    setShowRosModel(false);
    if (moderationModeRef.current) return;

    manageEmojisEverywhereInteraction();

    setOverrideDismissStatus(new Date().getTime());
  }, [currentRosFeature.id, startInteraction]);

  const onPauseClick = () => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    chart.series[0].chart.delImgs = [];
  };

  const deleteComment = async (text) => {
    const { charts } = Highcharts;
    const chart = charts[charts.length - 1];
    const tempComments = chart.series[0].data.map((comment) => comment.name);
    const index = tempComments.indexOf(text);
    if (index !== null) {
      const wordToDelete = chart.series[0].data[index];
      setExclusionList(wordToDelete?.name?.toLowerCase());
      wordToDelete?.graphic?.hide();
      return { status: true };
    }
  };

  const handleEditBtn = () => {
    if (isPaused) {
      setIsPaused(false); onPlayClick();
    } else {
      setIsPaused(true); onPauseClick();
    }
  };

  useImperativeHandle(ref, () => ({
    handleEditBtn,
    deleteComment,
    async triggerEngagement({ interactionResult }) {
      if (interactionResult && interactionResult.length > 0) {
        await handleEmojisEverywhere({ interactionId: interactionResult[0].id, interactionResult });
      }
    }
  }));

  useEffect(() => {
    if (!splitDiv) return;
    window.dispatchEvent(new Event('resize'));
    dispatch({ type: 'UPDATE_DATA', data: comments });
  }, [splitDiv]);

  return (
    <div className={'w-full h-full overflow-hidden rounded-lg flex'}>
      <WordChart
        backgroundColor={'rgba(255, 255, 255, 0)'}
        highchartsRef={highchartsRef}
        chartOptions={chartOptions}
        isFullScreen={isFullScreen}
        splitDiv={splitDiv}
        containerProps={{ style: { width: '100%', minHeight: '100%' } }}
        showInstantCustomisationPopup={showInstantCustomisationPopup}
      />
    </div>
  );
});

export default EmojisEverywhere;
